import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ShoppingBag } from "lucide-react";

export default function BundleDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  const [allOwned, setAllOwned] = useState(false);
  const { addBundle, hasBundle } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setErr(false);
    api.get(`/bundles/${slug}`).then((r) => setB(r.data)).catch(() => setErr(true));
  }, [slug]);

  useEffect(() => {
    if (user && b?.products?.length) {
      Promise.all(b.products.map((p) => api.get(`/library/owns/${p.product_id}`).then((r) => r.data.owned).catch(() => false)))
        .then((res) => setAllOwned(res.every(Boolean)));
    }
  }, [user, b]);

  if (err) {
    return (
      <Layout>
        <div className="container-narrow py-32 text-center" data-testid="bundle-not-found">
          <p className="font-serif text-2xl text-brand-ink">Bundle not found.</p>
          <Link to="/bundles" className="overline text-brand-rust mt-4 inline-block">Back to bundles →</Link>
        </div>
      </Layout>
    );
  }
  if (!b) return <Layout><div className="container-narrow py-32 overline text-brand-inkMuted">Loading…</div></Layout>;

  const handleAdd = () => {
    addBundle(b);
    toast.success("Bundle added to cart");
  };

  return (
    <Layout>
      <section className="container-narrow py-12 md:py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Link to="/bundles" className="overline text-brand-inkMuted hover:text-brand-ink" data-testid="back-to-bundles">← Back to bundles</Link>
          <div className="mt-6 aspect-[16/10] bg-brand-bg overflow-hidden border border-brand-line">
            <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" />
          </div>

          <div className="mt-12">
            <span className="overline text-brand-rust">About this bundle</span>
            <p className="font-serif text-2xl md:text-3xl mt-4 text-brand-ink leading-snug" data-testid="bundle-description">
              {b.description}
            </p>
          </div>

          <div className="mt-12">
            <span className="overline text-brand-rust">What's inside</span>
            <ul className="mt-5 divide-y divide-brand-line border border-brand-line bg-white">
              {b.products.map((p) => (
                <li key={p.product_id} className="px-5 py-4 flex items-center gap-4" data-testid={`bundle-item-${p.product_id}`}>
                  <div className="w-14 h-16 flex-none bg-brand-bg overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <Link to={`/p/${p.product_id}`} className="font-serif text-lg text-brand-ink hover:text-brand-rust leading-snug">{p.title}</Link>
                    <p className="text-xs text-brand-inkMuted mt-1 overline">{p.type} · {p.author || p.instructor}</p>
                  </div>
                  <span className="text-brand-inkMuted text-sm line-through">${Number(p.price).toFixed(0)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Don't forget the books — Amazon affiliate callout */}
          {b.matching_books && b.matching_books.length > 0 && (
            <div className="mt-12" data-testid="bundle-matching-books-section">
              <span className="overline text-brand-rust">Don't forget the books</span>
              <p className="mt-3 text-brand-inkMuted leading-relaxed">
                Each course in this bundle is based on a book. The bundle covers the courses — the books live on Amazon and pair beautifully alongside the video curriculum.
              </p>
              <ul className="mt-5 divide-y divide-brand-line border border-brand-line bg-white">
                {b.matching_books.map((bk) => (
                  <li
                    key={bk.product_id}
                    className="px-5 py-4 flex items-center gap-4"
                    data-testid={`bundle-book-${bk.product_id}`}
                  >
                    <div className="w-14 h-16 flex-none bg-brand-bg overflow-hidden">
                      <img src={bk.cover_image} alt={bk.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <Link to={`/p/${bk.product_id}`} className="font-serif text-lg text-brand-ink hover:text-brand-rust leading-snug">{bk.title}</Link>
                      <p className="text-xs text-brand-inkMuted mt-1 overline">book · {bk.author}</p>
                    </div>
                    {bk.amazon_url ? (
                      <a
                        href={bk.amazon_url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid={`bundle-book-amazon-${bk.product_id}`}
                        className="inline-flex items-center gap-1 overline text-brand-rust hover:text-brand-ink"
                      >
                        Buy on Amazon →
                      </a>
                    ) : (
                      <span className="overline text-brand-inkMuted">—</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* sticky right */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-white border border-brand-line p-8">
            {b.badge && <span className="overline text-brand-rust">{b.badge}</span>}
            <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05] text-brand-ink" data-testid="bundle-title">{b.title}</h1>
            {b.subtitle && <p className="text-brand-inkMuted mt-2">{b.subtitle}</p>}

            <div className="mt-8 border-t border-brand-line pt-6">
              <div className="flex items-center justify-between">
                <span className="overline text-brand-inkMuted">Bundle price</span>
                <span className="font-serif text-4xl text-brand-ink" data-testid="bundle-price">${b.bundle_price.toFixed(0)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-brand-inkMuted line-through">${b.list_total.toFixed(0)} list</span>
                <span className="overline text-brand-rust" data-testid="bundle-savings">You save ${b.savings.toFixed(0)} ({Math.round(b.discount_pct)}% off)</span>
              </div>
            </div>

            {allOwned ? (
              <button
                data-testid="bundle-open-library"
                onClick={() => navigate("/library")}
                className="mt-6 w-full bg-brand-ink text-white hover:bg-black py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2"
              >Open in library</button>
            ) : (
              <button
                data-testid="add-bundle-to-cart-btn"
                disabled={hasBundle(b.bundle_id)}
                onClick={handleAdd}
                className="mt-6 w-full bg-brand-rust text-white hover:bg-brand-rustDark py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                <ShoppingBag className="w-4 h-4" />
                {hasBundle(b.bundle_id) ? "Already in cart" : `Add bundle — $${b.bundle_price.toFixed(0)}`}
              </button>
            )}

            <ul className="mt-6 space-y-3 text-sm text-brand-inkMuted">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> All {b.products.length} items unlocked instantly</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Lifetime access</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-rust" /> Save ${b.savings.toFixed(0)} vs. buying separately</li>
            </ul>
          </div>
        </aside>
      </section>
    </Layout>
  );
}
